/**
 * El cliente de Supabase del navegador.
 *
 * Solo lleva la clave anónima, que es pública por diseño y viaja dentro del paquete. La clave de
 * servicio **jamás** llega aquí ni a ninguna variable con prefijo `VITE_` (principio VI).
 *
 * **El cliente se crea la primera vez que se usa, no al importar el módulo.** Importar no debe
 * reventar nunca: si lo hiciera, cualquier prueba de cualquier módulo que lo alcance de forma
 * transitiva —el autoguardado, por ejemplo— fallaría en un entorno sin configurar, aunque no
 * llegue a tocar la red. Es el mismo motivo por el que el servidor de juego crea su cliente de
 * forma perezosa.
 */

import { createClient } from '@supabase/supabase-js';

/** Clave con la que se guarda la sesión. Explícita para que las pruebas puedan sembrarla. */
export const AUTH_STORAGE_KEY = 'clica-auth';

function build() {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  // Vite deja las variables ausentes como `undefined`, no como cadena vacía: comprobar solo lo
  // segundo dejaría pasar el caso real y el fallo aparecería después, dentro de supabase-js.
  if (url === undefined || url === '' || anonKey === undefined || anonKey === '') {
    throw new Error(
      'Faltan VITE_SUPABASE_URL o VITE_SUPABASE_ANON_KEY. Copia .env.example a .env y rellénalas.',
    );
  }

  return createClient(url, anonKey, {
    db: { schema: 'clica' },
    auth: {
      // PKCE explícito: el valor por defecto de supabase-js sigue siendo el flujo implícito, que
      // deja el token en el fragmento de la URL.
      flowType: 'pkce',
      storageKey: AUTH_STORAGE_KEY,
      persistSession: true,
      autoRefreshToken: true,
      // La vuelta de Google se canjea sola al cargar la página.
      detectSessionInUrl: true,
    },
  });
}

/**
 * El tipo se deriva del constructor en lugar de escribirse a mano.
 *
 * `SupabaseClient` sin parámetros asume el schema `public`, y anotar con él haría que un cliente
 * apuntando a `clica` no encajara. Derivarlo mantiene el schema en el tipo.
 */
export type BrowserClient = ReturnType<typeof build>;

let client: BrowserClient | null = null;

/**
 * El cliente, creado al primer uso.
 *
 * El intermediario existe solo para retrasar la creación; a efectos de quien lo usa, es el
 * cliente de siempre.
 */
export const supabase: BrowserClient = new Proxy({} as BrowserClient, {
  get(_target, property, receiver) {
    client ??= build();
    return Reflect.get(client, property, receiver) as unknown;
  },
});
